import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { useAuthStore } from "../../../../zustand/loginStore";
import {
  createAuthenticatedAxiosInstance,
  createDeliveryAddress,
  fetchUserProfile,
  fetchPublicShippingCosts,
} from "../../../../api/auth";
import CustomSelect from "components/general/CustomSelect";
import { statesAndCities } from "../../../../constants/statesAndCitiesConstants";
import { IShippingCost } from "interfaces/IShippingCost";
import { useBreakpoints } from "hooks/useBreakPoints";
import MobileStateCityModal from "./MobileStateCityModal";
import "./AddAddressForm.scss";
import { Address } from "interfaces/IAddresses";

interface AddAddressFormProps {
  onAddressAdded: (address: Address) => void;
  onCancel: () => void;
  onReloadAddresses?: () => void;
}

/**
 * Form component for adding new delivery addresses
 * Handles address validation, state/city filtering based on shipping costs,
 * and address creation with proper error handling
 * @param {AddAddressFormProps} props - Component props
 * @returns {JSX.Element} The add address form component
 */
const AddAddressForm: React.FC<AddAddressFormProps> = ({
  onAddressAdded,
  onCancel,
  onReloadAddresses,
}) => {
  const { token } = useAuthStore();
  const { isMobile } = useBreakpoints();

  // Scroll hacia arriba cuando se abre el formulario
  useEffect(() => {
    // Buscar el elemento del formulario y hacer scroll hacia él
    const formElement = document.querySelector('.add-address-form');
    if (formElement) {
      formElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  const [userId, setUserId] = useState<number | null>(null);
  const [filteredStatesAndCities, setFilteredStatesAndCities] = useState<{
    [key: string]: string[];
  }>({});
  const [cities, setCities] = useState<string[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [modalType, setModalType] = useState<"state" | "city">("state");

  /**
   * Effect hook to fetch user profile and extract user ID
   */
  useEffect(() => {
    const getUserProfile = async () => {
      try {
        const axiosInstance = createAuthenticatedAxiosInstance(token);
        const profileData = await fetchUserProfile(axiosInstance);
        if (profileData && profileData.user && profileData.user.id) {
          setUserId(profileData.user.id);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    getUserProfile();
  }, [token]);

  /**
   * Effect hook to load and filter states based on active shipping costs
   * Only states with active shipping costs are available for selection
   */
  useEffect(() => {
    const loadAndFilterStates = async () => {
      try {
        setIsLoading(true);

        const shippingCosts = await fetchPublicShippingCosts();
        const activeStates = shippingCosts
          .filter((cost: IShippingCost) => cost.status === 1)
          .map((cost: IShippingCost) => cost.state);

        const filteredStates: { [key: string]: string[] } = {};

        activeStates.forEach((activeState: string) => {
          if (statesAndCities[activeState]) {
            filteredStates[activeState] = statesAndCities[activeState];
          } else {
            const matchingState = Object.keys(statesAndCities).find(
              (stateKey) =>
                stateKey.toLowerCase().trim() ===
                activeState.toLowerCase().trim()
            );

            if (matchingState) {
              filteredStates[matchingState] = statesAndCities[matchingState];
            } else {
              console.warn(
                `Estado "${activeState}" no encontrado en constantes`
              );
            }
          }
        });

        setFilteredStatesAndCities(filteredStates);
      } catch (error) {
        console.error("Error loading shipping costs:", error);
        setFilteredStatesAndCities(statesAndCities);
      } finally {
        setIsLoading(false);
      }
    };

    loadAndFilterStates();
  }, []);

  const [newAddress, setNewAddress] = useState({
    street: "",
    exterior_number: "",
    interior_number: "",
    neighborhood: "",
    postal_code: "",
    state: "",
    city: "",
    delivery_person: "",
    phone: "",
    references: "",
  });

  /**
   * Effect hook to update available cities when state selection changes
   * Clears city selection if the currently selected city is not available in the new state
   */
  useEffect(() => {
    if (newAddress.state && filteredStatesAndCities[newAddress.state]) {
      setCities(filteredStatesAndCities[newAddress.state]);

      if (
        newAddress.city &&
        !filteredStatesAndCities[newAddress.state].includes(newAddress.city)
      ) {
        setNewAddress((prev) => ({ ...prev, city: "" }));
      }
    } else {
      setCities([]);
    }
  }, [newAddress.state, filteredStatesAndCities]);

  /**
   * Handles input changes for form fields
   * Clears field-specific errors when user starts editing
   * @param {React.ChangeEvent} e - The change event from form inputs
   */
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    const updatedValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setNewAddress((prev) => ({ ...prev, [name]: updatedValue }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleMobileStateSelect = (state: string) => {
    setNewAddress((prev) => ({
      ...prev,
      state: state,
      city: "",
    }));
  };

  const handleMobileCitySelect = (city: string) => {
    setNewAddress((prev) => ({
      ...prev,
      city: city,
    }));
  };

  const openMobileModal = (type: "state" | "city") => {
    // Si intentan abrir el modal de ciudad pero no hay estado seleccionado,
    // abrir el modal de estado en su lugar
    if (type === "city" && !newAddress.state) {
      setModalType("state");
      setShowMobileModal(true);
    } else {
      setModalType(type);
      setShowMobileModal(true);
    }
  };

  /**
   * Validates all form fields and returns validation result
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!newAddress.street.trim()) {
      errors.street = "La calle es obligatoria";
    }

    if (!newAddress.exterior_number.trim()) {
      errors.exterior_number = "El número exterior es obligatorio";
    }

    if (!newAddress.neighborhood.trim()) {
      errors.neighborhood = "La colonia es obligatoria";
    }

    if (!newAddress.postal_code.trim()) {
      errors.postal_code = "El código postal es obligatorio";
    } else if (!/^\d{5}$/.test(newAddress.postal_code)) {
      errors.postal_code = "El código postal debe tener 5 dígitos";
    }

    if (!newAddress.state) {
      errors.state = "El estado es obligatorio";
    }

    if (!newAddress.city) {
      errors.city = "La ciudad es obligatoria";
    }

    if (!newAddress.phone.trim()) {
      errors.phone = "El teléfono es obligatorio";
    } else if (!/^\d{10}$/.test(newAddress.phone)) {
      errors.phone = "El teléfono debe tener 10 dígitos";
    }

    if (!newAddress.delivery_person.trim()) {
      errors.delivery_person =
        "El nombre de la persona de entrega es obligatorio";
    } else if (newAddress.delivery_person.trim().length < 2) {
      errors.delivery_person =
        "El nombre debe ser válido (mínimo 2 caracteres)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handles form submission
   * Validates form data, creates address via API, and handles success/error states
   */
  const handleSubmitClick = async () => {
    if (!validateForm()) {
      return;
    }

    if (!userId) {
      setFormErrors({
        submit:
          "No se pudo obtener la información del usuario. Por favor, inténtelo de nuevo.",
      });
      return;
    }

    try {
      const axiosInstance = createAuthenticatedAxiosInstance(token);

      const addressData: Partial<Address> = {
        user_id: userId,
        address: newAddress.street,
        external_number: parseInt(newAddress.exterior_number) || 0,
        internal_number: parseInt(newAddress.interior_number) || 0,
        neighborhood: newAddress.neighborhood,
        postal_code: newAddress.postal_code,
        state: newAddress.state,
        city: newAddress.city,
        phone: newAddress.phone,
        delivery_person: newAddress.delivery_person,
        references: newAddress.references,
        is_default: isDefault ? 1 : 0,
      };

      const response = await createDeliveryAddress(
        axiosInstance,
        addressData as Address
      );

      if (response && response.address) {
        onAddressAdded(response.address);
        if (onReloadAddresses) {
          onReloadAddresses();
        }
        onCancel();
      } else {
        throw new Error("Formato de respuesta inesperado");
      }
    } catch (error) {
      console.error("Error al añadir dirección:", error);
      setFormErrors({
        submit: "Error al guardar la dirección. Por favor, inténtelo de nuevo.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">
            Cargando estados disponibles...
          </span>
        </div>
        <p className="mt-2">Verificando estados disponibles para envío...</p>
      </div>
    );
  }

  /**
   * Creates options array for state selection dropdown
   */
  const availableStatesOptions = Object.keys(filteredStatesAndCities)
    .sort()
    .map((state) => ({
      value: state,
      label: state,
    }));

  return (
    <div className="add-address-form">
      <Form.Group controlId="formStreet" className="mb-3">
        <Form.Label>Calle</Form.Label>
        <Form.Control
          type="text"
          name="street"
          value={newAddress.street}
          onChange={handleInputChange}
          placeholder="Nombre de la calle"
          isInvalid={!!formErrors.street}
        />
        <Form.Control.Feedback type="invalid">
          {formErrors.street}
        </Form.Control.Feedback>
      </Form.Group>

      <Row>
        <Col md={6}>
          <Form.Group controlId="formExteriorNumber" className="mb-3">
            <Form.Label>Número externo</Form.Label>
            <Form.Control
              type="text"
              name="exterior_number"
              value={newAddress.exterior_number}
              onChange={handleInputChange}
              placeholder="Número exterior"
              isInvalid={!!formErrors.exterior_number}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.exterior_number}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="formInteriorNumber" className="mb-3">
            <Form.Label>Número interno</Form.Label>
            <Form.Control
              type="text"
              name="interior_number"
              value={newAddress.interior_number}
              onChange={handleInputChange}
              placeholder="Número interior, depto, etc."
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group controlId="formNeighborhood" className="mb-3">
            <Form.Label>Colonia</Form.Label>
            <Form.Control
              type="text"
              name="neighborhood"
              value={newAddress.neighborhood}
              onChange={handleInputChange}
              placeholder="Colonia o fraccionamiento"
              isInvalid={!!formErrors.neighborhood}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.neighborhood}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="formPostalCode" className="mb-3">
            <Form.Label>Código postal</Form.Label>
            <Form.Control
              type="text"
              name="postal_code"
              value={newAddress.postal_code}
              onChange={handleInputChange}
              placeholder="Código postal (5 dígitos)"
              isInvalid={!!formErrors.postal_code}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.postal_code}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group controlId="formState" className="mb-3">
            <Form.Label>Estado</Form.Label>
            {isMobile ? (
              <div
                className="mobile-select-button"
                onClick={() => openMobileModal("state")}
              >
                <span className={newAddress.state ? "selected-value" : "placeholder"}>
                  {newAddress.state || "Selecciona un estado"}
                </span>
                <span className="dropdown-arrow">▼</span>
              </div>
            ) : (
              <CustomSelect
                options={availableStatesOptions}
                value={
                  newAddress.state
                    ? {
                        value: newAddress.state,
                        label: newAddress.state,
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  const selectedState = selectedOption?.value || "";
                  setNewAddress((prev) => ({
                    ...prev,
                    state: selectedState,
                    city: "",
                  }));
                }}
                placeholder="Selecciona un estado"
                isFilteredIconVisible={false}
                singleValueFontSize={isMobile ? "20px" : "28px"}
                optionFontSize={isMobile ? "18px" : "24px"}
                maxMenuHeight={isMobile ? 300 : 200}
              />
            )}
            {formErrors.state && (
              <div className="invalid-feedback d-block">{formErrors.state}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group controlId="formCity" className="mb-3">
            <Form.Label>Ciudad</Form.Label>
            {isMobile ? (
              <div
                className="mobile-select-button"
                onClick={() => openMobileModal("city")}
              >
                <span className={newAddress.city ? "selected-value" : "placeholder"}>
                  {newAddress.city || (newAddress.state ? "Selecciona una ciudad" : "Selecciona un estado primero")}
                </span>
                <span className="dropdown-arrow">▼</span>
              </div>
            ) : (
              <CustomSelect
                options={cities.map((city) => ({
                  value: city,
                  label: city,
                }))}
                value={
                  newAddress.city
                    ? {
                        value: newAddress.city,
                        label: newAddress.city,
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  const selectedCity = selectedOption?.value || "";
                  setNewAddress((prev) => ({
                    ...prev,
                    city: selectedCity,
                  }));
                }}
                placeholder="Selecciona una ciudad"
                isFilteredIconVisible={false}
                singleValueFontSize={isMobile ? "20px" : "28px"}
                optionFontSize={isMobile ? "18px" : "24px"}
                maxMenuHeight={isMobile ? 300 : 200}
              />
            )}
            {formErrors.city && (
              <div className="invalid-feedback d-block">{formErrors.city}</div>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group controlId="formDeliveryPerson" className="mb-3">
            <Form.Label>Persona de entrega </Form.Label>
            <Form.Control
              type="text"
              name="delivery_person"
              value={newAddress.delivery_person}
              onChange={handleInputChange}
              placeholder="Nombre de la persona de entrega"
              isInvalid={!!formErrors.delivery_person}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.delivery_person}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="formPhone" className="mb-3">
            <Form.Label>Teléfono de entrega</Form.Label>
            <Form.Control
              type="tel"
              name="phone"
              value={newAddress.phone}
              onChange={handleInputChange}
              placeholder="Teléfono de la persona de entrega"
              maxLength={10}
              isInvalid={!!formErrors.phone}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.phone}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group controlId="formReferences" className="mb-3">
        <Form.Label>Referencias (opcional)</Form.Label>
        <Form.Control
          as="textarea"
          name="references"
          value={newAddress.references}
          onChange={handleInputChange}
          placeholder="Color de la casa, puntos de referencia, etc."
          rows={3}
        />
      </Form.Group>

      {formErrors.submit && (
        <div className="alert alert-danger mb-3">{formErrors.submit}</div>
      )}

      <div className="button-container">
        <Button
          variant="outline-secondary"
          onClick={onCancel}
          className="cancel-button"
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          className="save-button"
          onClick={handleSubmitClick}
        >
          Añadir
        </Button>
      </div>

      {/* Modal móvil para selección de estado/ciudad */}
      <MobileStateCityModal
        show={showMobileModal}
        onHide={() => setShowMobileModal(false)}
        onStateSelect={handleMobileStateSelect}
        onCitySelect={handleMobileCitySelect}
        selectedState={newAddress.state}
        selectedCity={newAddress.city}
        availableStates={Object.keys(filteredStatesAndCities)}
        availableCities={cities}
        title={modalType === "state" ? "Seleccionar Estado" : "Seleccionar Ciudad"}
        type={modalType}
      />
    </div>
  );
};

export default AddAddressForm;
